/**
 * BuyCreditsProvider — Push 4-1ah (2026-07-10) frictionless top-up primitive.
 *
 * Ean's frame: "many ways to top off and buy credits instead of having to
 * click from one area to a next, easy to work with, use, and spend money for
 * a 60k admin (power user or standard), same goes for the end user power
 * user and standard user."
 *
 * Mounts BuyCreditsModal (admin/owner branch — Stripe checkout) and
 * RequestTopupModalInline (user branch — request-approval flow) at the top
 * of the app once, and exposes a `useBuyCredits()` hook so any surface (chat
 * pill, home widget, settings, banner, error-recovery toast action) can
 * open the right modal in one line:
 *
 *   const { open } = useBuyCredits();
 *   <button onClick={() => open('proactive')}>Top up</button>
 *
 * Role branch is decided at open-time from the current session:
 *   - owner / admin → BuyCreditsModal (bundle picker + Stripe checkout)
 *   - user          → RequestTopupModalInline ("Ask your admin" flow)
 *
 * Kept intentionally simple — no state machine, no history. Just:
 *   open(reason?) → picks the right modal and shows it.
 *
 * The `reason` argument is threaded into the subtitle copy so the modal
 * feels contextual (low_balance | error_recovery | proactive).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useSession } from '../hooks/useSession.tsx';
import { BuyCreditsModal } from './BuyCreditsModal.tsx';
import {
  ApiError,
  fetchAdminContact,
  type AdminContact,
} from '../lib/api.ts';
import { useToast } from './Toast.tsx';
// Push 4-1aj (2026-07-10) — RequestModal handles the olytic:contact-admin
// custom event fired by the 403 recovery toast in Toast.tsx. Same shape as
// the buy-credits listener below: decouples the toast layer from React
// context so any surface firing showApiErrorToast() gets a working
// "Contact admin →" action without importing RequestModal directly.
import { RequestModal } from './RequestModal.tsx';

export type BuyCreditsReason = 'low_balance' | 'error_recovery' | 'proactive';

interface BuyCreditsContextValue {
  /** Opens the appropriate credit-topup modal for the current user's role. */
  open: (reason?: BuyCreditsReason) => void;
}

const BuyCreditsContext = createContext<BuyCreditsContextValue | undefined>(
  undefined,
);

export function BuyCreditsProvider({ children }: { children: ReactNode }) {
  const { user } = useSession();
  const [state, setState] = useState<{
    open: boolean;
    reason: BuyCreditsReason;
  }>({ open: false, reason: 'proactive' });
  // Push 4-1aj (2026-07-10) — contact-admin RequestModal state. Fires when
  // Toast.tsx's showApiErrorToast() 403 recovery action dispatches the
  // olytic:contact-admin custom event.
  const [contactAdminOpen, setContactAdminOpen] = useState(false);

  const open = useCallback((reason: BuyCreditsReason = 'proactive') => {
    setState({ open: true, reason });
  }, []);

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const value = useMemo<BuyCreditsContextValue>(() => ({ open }), [open]);

  const isPrivileged = user?.role === 'owner' || user?.role === 'admin';

  // Push 4-1ai (2026-07-10): listen for the `olytic:buy-credits` custom
  // event fired by Toast.tsx's default 402 recovery action. Decouples
  // the toast layer from React context so any showApiErrorToast callsite
  // gets a working "Buy credits →" button without importing this
  // provider directly.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (): void => {
      open('error_recovery');
    };
    window.addEventListener('olytic:buy-credits', handler);
    return () => window.removeEventListener('olytic:buy-credits', handler);
  }, [open]);

  // Push 4-1aj (2026-07-10): listen for the `olytic:contact-admin` custom
  // event (dispatched by Toast.tsx's 403 recovery action). Opens the shared
  // RequestModal with destination='admin' — same modal used by TopNav's
  // Help > Contact Your Admin. Hosting the listener here (rather than a
  // sibling ContactAdminProvider) is intentional: this provider already
  // mounts once at the app root and owns the buy-credits recovery loop,
  // so co-locating the contact-admin recovery loop keeps the "toast
  // recovery actions all live in one place" mental model coherent.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (): void => {
      setContactAdminOpen(true);
    };
    window.addEventListener('olytic:contact-admin', handler);
    return () => window.removeEventListener('olytic:contact-admin', handler);
  }, []);

  return (
    <BuyCreditsContext.Provider value={value}>
      {children}
      {/* Push 4-1ah (2026-07-10): one global mount. Role-branched at open
         time; the alternative was mounting BOTH modals conditionally and
         letting each caller decide, which forces every consumer to know
         about role. The hook shape hides that decision entirely. */}
      {isPrivileged ? (
        <BuyCreditsModal
          open={state.open}
          onClose={close}
          showTestCreditsHint
        />
      ) : (
        <RequestTopupModalInline
          open={state.open}
          reason={state.reason}
          onClose={close}
        />
      )}
      {/* Push 4-1aj (2026-07-10) — RequestModal for the olytic:contact-admin
         recovery event. destination='admin' routes to the "Contact Your
         Admin" flow (same as TopNav Help > Contact Your Admin). */}
      <RequestModal
        open={contactAdminOpen}
        onClose={() => setContactAdminOpen(false)}
        destination="admin"
      />
    </BuyCreditsContext.Provider>
  );
}

export function useBuyCredits(): BuyCreditsContextValue {
  const ctx = useContext(BuyCreditsContext);
  if (ctx === undefined) {
    // Defensive fallback: if someone tries to call useBuyCredits() outside
    // the provider (e.g. in a marketing page or storybook), give them a
    // no-op instead of throwing. Silent noop is friendlier than a crash
    // for a "topup" affordance that isn't critical to page rendering.
    return { open: () => undefined };
  }
  return ctx;
}

// ---------------------------------------------------------------------
// User-branch modal — "Ask your workspace admin to add credits"
// ---------------------------------------------------------------------

interface RequestTopupModalInlineProps {
  open: boolean;
  reason: BuyCreditsReason;
  onClose: () => void;
}

/**
 * The user-branch modal. Uses the existing
 * POST /api/me/wallet/request-approval endpoint (already used by
 * ChatApprovalBanner). Displays the admin contact so the user knows who
 * will receive the request.
 */
function RequestTopupModalInline({
  open,
  reason,
  onClose,
}: RequestTopupModalInlineProps) {
  const [amount, setAmount] = useState<number>(500);
  const [note, setNote] = useState<string>('');
  const [adminContact, setAdminContact] = useState<AdminContact | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Push 4-1an (2026-07-10) — pull the user's individual avg daily burn so
  // the modal can show "You currently spend ~X credits/day. {N} credits ≈
  // {N/burn} days runway." context above the amount input. Best-effort;
  // hides the caption if the fetch fails or burn is 0.
  const [avgDailyBurn, setAvgDailyBurn] = useState<number | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (!open) return;
    // Reset form state every open.
    setAmount(500);
    setNote('');
    setSubmitted(false);
    setAvgDailyBurn(null);
    // Lazy-load the admin contact so the modal shows who receives the
    // request. Non-blocking — modal still works if this fails.
    let cancelled = false;
    fetchAdminContact()
      .then((res) => {
        if (!cancelled) setAdminContact(res.contact);
      })
      .catch(() => {
        /* silent */
      });
    // Push 4-1an (2026-07-10) — best-effort wallet fetch for the runway
    // context caption. Reads user.individual_avg_daily_burn_credits_14d.
    fetch('/api/me/wallet', { credentials: 'include' })
      .then(async (resp) => {
        if (!resp.ok) return;
        const data = (await resp.json()) as {
          user?: { individual_avg_daily_burn_credits_14d?: number };
        };
        if (cancelled) return;
        const burn = data.user?.individual_avg_daily_burn_credits_14d;
        setAvgDailyBurn(
          typeof burn === 'number' && Number.isFinite(burn) && burn > 0
            ? burn
            : null,
        );
      })
      .catch(() => {
        /* silent */
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, submitting]);

  if (!open) return null;

  async function submit() {
    if (amount <= 0) return;
    setSubmitting(true);
    try {
      const resp = await fetch('/api/me/wallet/request-approval', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          requested_credits: amount,
          request_context: { triggered_from: `topup_modal_${reason}` },
          ...(note.trim() !== '' ? { note: note.trim() } : {}),
        }),
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as {
          message?: string;
        };
        throw new ApiError(resp.status, body.message ?? 'Could not send request.');
      }
      setSubmitted(true);
      showToast({
        kind: 'success',
        message:
          adminContact !== null
            ? `Request sent to ${adminContact.display_name}. You'll get a notification when they respond.`
            : "Request sent. You'll get a notification when your admin responds.",
      });
    } catch (err) {
      showToast({
        kind: 'error',
        message:
          err instanceof ApiError
            ? err.message
            : 'Could not send request. Try again in a moment.',
      });
    } finally {
      setSubmitting(false);
    }
  }

  const subtitle =
    reason === 'low_balance'
      ? 'Your balance is running low. Send your admin a top-up request.'
      : reason === 'error_recovery'
        ? "You've run out of credits. Send your admin a top-up request to keep going."
        : 'Your workspace credits are topped up by an admin. Send a request and they will get a notification.';

  const quickPicks = [100, 500, 1000, 5000];

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (submitting) return;
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-card" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2 className="modal-title">
            {submitted ? 'Request sent' : 'Ask your workspace admin for credits'}
          </h2>
          <button
            type="button"
            className="modal-close"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {submitted ? (
            <div>
              <p className="muted" style={{ fontSize: 13, lineHeight: 1.55, margin: '0 0 12px' }}>
                {adminContact !== null ? (
                  <>
                    <strong style={{ color: 'var(--olytic-obsidian)' }}>
                      {adminContact.display_name}
                    </strong>{' '}
                    will see your request in their notifications and can top up
                    your balance from there.
                  </>
                ) : (
                  <>
                    Your admin will see your request in their notifications and
                    can top up your balance from there.
                  </>
                )}
              </p>
            </div>
          ) : (
            <div>
              <p
                className="muted"
                style={{ fontSize: 13, lineHeight: 1.55, margin: '0 0 16px' }}
              >
                {subtitle}
              </p>

              {adminContact !== null && adminContact.email !== null && (
                <div
                  className="callout"
                  style={{
                    background: 'color-mix(in srgb, var(--olytic-teal) 4%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--olytic-teal) 16%, transparent)',
                    borderRadius: 8,
                    padding: '10px 12px',
                    marginBottom: 16,
                    fontSize: 12,
                  }}
                >
                  <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>
                    Your admin
                  </div>
                  <div style={{ fontWeight: 600 }}>{adminContact.display_name}</div>
                  <div className="muted">{adminContact.email}</div>
                </div>
              )}

              {/* Push 4-1an (2026-07-10) — runway context caption. Shows
                 the user's recent burn rate + projected runway for the
                 currently-selected amount. Skipped when burn is unknown
                 (new user, no 14-day history). */}
              {avgDailyBurn !== null && (
                <div
                  className="muted"
                  style={{
                    fontSize: 12,
                    lineHeight: 1.5,
                    marginBottom: 12,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  You currently spend ~{Math.max(1, Math.round(avgDailyBurn))} credits/day.
                  {' '}
                  {amount > 0 && (
                    <>
                      {amount.toLocaleString()} credits ≈{' '}
                      {Math.max(1, Math.floor(amount / avgDailyBurn))} days of runway.
                    </>
                  )}
                </div>
              )}
              <label
                style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: 'var(--olytic-obsidian)',
                }}
              >
                How many credits?
              </label>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                {quickPicks.map((n) => {
                  const isSelected = amount === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setAmount(n)}
                      className="card"
                      style={{
                        padding: '10px 8px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: `${isSelected ? '2px' : '1px'} solid ${
                          isSelected
                            ? 'var(--olytic-teal)'
                            : 'var(--surface-border, #e5e7eb)'
                        }`,
                        background: isSelected
                          ? 'rgba(20, 184, 166, 0.06)'
                          : 'var(--surface-bg, #fff)',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        margin: isSelected ? '-1px' : 0,
                      }}
                    >
                      {n.toLocaleString()}
                    </button>
                  );
                })}
              </div>
              <input
                type="number"
                min={1}
                step={50}
                value={amount}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v) && v > 0) setAmount(v);
                }}
                aria-label="Custom credit amount"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  marginBottom: 16,
                }}
              />

              <label
                style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: 'var(--olytic-obsidian)',
                }}
              >
                Why (optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What are you working on that needs more credits?"
                rows={3}
                maxLength={500}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
            </div>
          )}
        </div>

        <div className="modal-footer">
          {submitted ? (
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void submit()}
                disabled={submitting || amount <= 0}
              >
                {submitting ? 'Sending…' : 'Send request'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
